// src/services/diagnosisService.ts
import axiosInstance from '../config/axiosInstance';
import { format } from 'date-fns';
import { QueryFunctionContext } from '@tanstack/react-query';

// --- Tipos de Datos de la API de Diagnóstico v4.0 (Arquitectura Interactiva) ---

// Tipos compartidos
interface KPISet {
    totalRevenue: number | null; totalGrossProfit: number | null; averageMarginPercentage: number | null;
    uniqueCustomers: number | null; daysSinceLastPurchase: number | null; totalTransactions: number | null;
    averageTicketValue: number | null; uniqueProductsSold: number | null; averageQuantityPerTransaction: number | null;
    newCustomers: number | null; recurrentCustomers: number | null; averagePurchaseFrequencyDays: number | null;
    customerLifetimeValue: number | null; customerAveragePurchaseFrequencyDays: number | null;
    customerLifetimeValueRank: number | null; totalRevenueSeries?: (number | null)[];
    totalGrossProfitSeries?: (number | null)[]; averageMarginPercentageSeries?: (number | null)[];
    uniqueCustomersSeries?: (number | null)[];
}

// /kpis
export interface TransactionKPIDataResponse {
    current_period: KPISet;
    comparison_period: KPISet | null;
    firstTransactionDateAvailable: string | null;
    lastTransactionDateAvailable: string | null;
}

// /sales-over-time
interface ComparativeSalesPoint {
  display_label: string;
  normalized_date: string;
  current_revenue: number | null;
  comparison_revenue: number | null;
}

export interface ComparativeSalesOverTimeResponse {
  granularity: 'day' | 'week' | 'month';
  data: ComparativeSalesPoint[];
}

// /profitability/top-entities
interface ComparativeEntityItem { name: string; current_value: number; comparison_value: number | null; }
export interface TopEntitiesResponse { top_products_by_profit: ComparativeEntityItem[]; top_customers_by_revenue: ComparativeEntityItem[]; }

// /customers-list
interface CustomerInfo { id: string; label: string; }
export interface CustomerListResponse { customers: CustomerInfo[]; }

// /customer-intelligence/{customer_id}
interface ProfitTrendPoint { display_label: string; normalized_date: string; current_profit: number | null; comparison_profit: number | null; }
export interface CustomerIntelligenceResponse { current_average_days_between_transactions: number | null; comparison_average_days_between_transactions: number | null; profit_trend: ProfitTrendPoint[]; }

// /intelligence/summary
interface AtRiskClientItem { customer_id: string; total_revenue: number; historical_avg_margin_percentage: number | null; days_since_last_transaction: number; avg_days_between_transactions: number | null; transactions_current_period: number; transactions_comparison_period: number; }
export interface IntelligenceSummaryResponse { at_risk_clients: AtRiskClientItem[]; }

// /customer-value-distribution
interface QuintileRevenuePoint { month_label: string; q1_revenue: number; q2_revenue: number; q3_revenue: number; q4_revenue: number; q5_revenue: number; }
export interface CustomerValueDistributionResponse { current_period_data: QuintileRevenuePoint[]; comparison_period_data: QuintileRevenuePoint[]; }

// /product-performance-matrix
interface ProductPerformancePoint { product_id: string; total_quantity_sold: number; average_margin_percentage: number; total_revenue: number; revenue_quintile: number; }
export interface ProductPerformanceMatrixResponse { data: ProductPerformancePoint[]; }

// /customer-quintile-analysis
interface QuintileMigrationSummary { new_customers: number; lost_customers: number; promoted_customers: number; demoted_customers: number; maintained_customers: number; total_customers_current: number; total_customers_comparison: number; }
interface QuintileAnalysisDetail { quintile: string; customer_count: number; total_profit: number; profit_per_customer: number; percentage_of_total_profit: number; }
interface CustomerQuintileHistoryPoint { period: string; quintile: number; }
interface CustomerPerformanceComparison { metric: string; current_value: number; comparison_value: number; }
export interface CustomerQuintileAnalysisResponse { migration_matrix?: number[][]; summary?: QuintileMigrationSummary; quintile_labels?: string[]; current_period_analysis?: QuintileAnalysisDetail[]; customer_quintile_history?: CustomerQuintileHistoryPoint[]; customer_performance_comparison?: CustomerPerformanceComparison[]; }

// --- Lógica de Construcción de Parámetros ---

const buildBaseParams = (queryKey: any): URLSearchParams => {
    const [_key, _key2, dateRanges, selectedCustomerId] = queryKey;
    
    // --- INICIO DE MODIFICACIÓN: CONTRATO DE ACERO ---
    // La bandera 'enabled' en el hook useQuery es la guarda principal.
    // Si esta función es llamada sin fechas válidas, indica un error de lógica.
    // Lanzar un error aquí hará que la query falle ruidosamente en lugar de enviar una mala petición.
    if (!dateRanges?.current?.startDate || !dateRanges?.current?.endDate) {
        throw new Error("buildBaseParams fue llamado con un rango de fechas inválido. Esto indica un error de lógica en el componente que lo llama.");
    }
    // --- FIN DE MODIFICACIÓN ---

    const params = new URLSearchParams({
        start_date: format(dateRanges.current.startDate, 'yyyy-MM-dd'),
        end_date: format(dateRanges.current.endDate, 'yyyy-MM-dd'),
    });

    if (selectedCustomerId) {
        params.append('customer_id', selectedCustomerId);
    }

    if (dateRanges.comparison?.startDate) {
        params.append('compare_start_date', format(dateRanges.comparison.startDate, 'yyyy-MM-dd'));
        params.append('compare_end_date', format(dateRanges.comparison.endDate, 'yyyy-MM-dd'));
    }
    return params;
};


// --- NUEVO: Tipos para Auditoría Forense (Profit Leakage) ---

export interface WaterfallStep {
    id: string;
    label: string;
    value: number;
    color: string;
    type: 'base' | 'increment' | 'subtotal' | 'final';
}

export interface AuditSummary {
    total_real_margin: number;
    total_potential_mei: number;
    leakage_strategy: number;
    leakage_gap_mei: number;
    audit_date: string;
}

export interface OpportunityItem {
    product_id: string;
    customer_id: string;
    month: string;
    real_price: number;
    ai_price: number;
    margin_gain: number;
    vol_impact: number;
}

export interface ProfitLeakageReportResponse {
    summary: AuditSummary;
    waterfall_chart: WaterfallStep[];
    top_opportunities: OpportunityItem[];
}

// --- Funciones de Servicio (Alineadas con la nueva arquitectura) ---

export const getCustomerList = async (): Promise<CustomerListResponse> => {
    const { data } = await axiosInstance.get('/diagnosis/customers-list');
    return data;
};

export const getTransactionKPIs = async (context: QueryFunctionContext) => {
    const params = buildBaseParams(context.queryKey);
    const { data } = await axiosInstance.get(`/diagnosis/kpis?${params.toString()}`);
    return data;
};

export const getSalesOverTime = async (
  context: QueryFunctionContext
): Promise<ComparativeSalesOverTimeResponse> => {
  const params = buildBaseParams(context.queryKey);
  params.append('granularity', 'auto');

  const { data } = await axiosInstance.get(`/diagnosis/sales-over-time?${params.toString()}`);
  return data;
};

export const getTopEntities = async (context: QueryFunctionContext) => {
    const params = buildBaseParams(context.queryKey);
    params.append('limit', '5');
    const { data } = await axiosInstance.get(`/diagnosis/profitability/top-entities?${params.toString()}`);
    return data;
};

export const getCustomerIntelligence = async (context: QueryFunctionContext) => {
    const [_key, _key2, _dateRanges, selectedCustomerId] = context.queryKey as [string, string, any, string | null];
    if (!selectedCustomerId) throw new Error("Customer ID es requerido para esta consulta.");
    
    const params = buildBaseParams(context.queryKey);
    const { data } = await axiosInstance.get(`/diagnosis/customer-intelligence/${selectedCustomerId}?${params.toString()}`);
    return data;
};

export const getIntelligenceSummary = async (context: QueryFunctionContext): Promise<IntelligenceSummaryResponse> => {
    const params = buildBaseParams(context.queryKey);
    const { data } = await axiosInstance.get(`/diagnosis/intelligence/summary?${params.toString()}`);
    return data;
};

export const getCustomerValueDistribution = async (context: QueryFunctionContext): Promise<CustomerValueDistributionResponse> => {
    const params = buildBaseParams(context.queryKey);
    const { data } = await axiosInstance.get(`/diagnosis/customer-value-distribution?${params.toString()}`);
    return data;
};

export const getProductPerformanceMatrix = async (context: QueryFunctionContext): Promise<ProductPerformanceMatrixResponse> => {
    const params = buildBaseParams(context.queryKey);
    const { data } = await axiosInstance.get(`/diagnosis/product-performance-matrix?${params.toString()}`);
    return data;
};

export const getCustomerQuintileAnalysis = async (context: QueryFunctionContext): Promise<CustomerQuintileAnalysisResponse> => {
    const params = buildBaseParams(context.queryKey); 
    const { data } = await axiosInstance.get(`/diagnosis/customer-quintile-analysis?${params.toString()}`);
    return data;
};

export const getProfitLeakageReport = async (): Promise<ProfitLeakageReportResponse> => {
    // Nota Arquitectónica: Este endpoint consulta la tabla persistente 'audit_profit_leakage'.
    // No requiere filtros de fecha dinámicos (start_date) del frontend, ya que refleja 
    // la última ejecución del Worker de Auditoría.
    const { data } = await axiosInstance.get('/diagnosis/profit-leakage/report');
    return data;
};

// --- AÑADIR A frontend/src/services/diagnosisService.ts ---

// 1. Definición del Tipo de Respuesta (Interface)
export interface SystemStatusResponse {
    data_points: number;
    transactions_audited: number;
    features_per_tx: number;
    last_ingestion: string | null;
    next_training: string;
    ai_status: string;
}

// 2. La Función de Llamada (Endpoint)
export const getSystemStatus = async (): Promise<SystemStatusResponse> => {
    // Asumimos que el backend montó el router en /diagnosis
    const response = await axiosInstance.get<SystemStatusResponse>('/diagnosis/system/status');
    return response.data;
};

export interface RetrainingStatusResponse {
    status: 'ready_to_train' | 'pending' | 'running' | 'success' | 'success_with_warnings' | 'error' | 'not_found';
    can_train: boolean;
    started_at?: string | null;
    finished_at?: string | null;
    next_training_available_at: string | null;
    error_message?: string;
    // PRC-041: ya hay un modelo entrenado con éxito (la optimización funciona aunque se reentrene).
    has_ready_model?: boolean;
}

export const getRetrainingStatus = async (): Promise<RetrainingStatusResponse> => {
    const { data } = await axiosInstance.get('/retraining/status');
    return data;
};

export interface ModulePulse {
    status: 'pending' | 'running' | 'success' | 'failed' | 'idle';
    last_run_at: string | null;
    next_scheduled_run: string | null;
}

export interface SystemPulseResponse {
    tier: string;
    global_status: 'idle' | 'processing' | 'error';
    modules: {
        DATA_INGEST: ModulePulse;
        PRICING_ENGINE: ModulePulse;
        DEMAND_FORECAST: ModulePulse;
        FUTURE_SIMULATION: ModulePulse;
    };
}

export const getSystemPulse = async (): Promise<SystemPulseResponse> => {
    const response = await axiosInstance.get('/system/pulse');
    return response.data;
};